import React, { useState } from 'react';
import { FiPlus } from "react-icons/fi";
import { RATE_RATIOS } from '../utilities/seasonSettingsForm';
import NewApplicationForm from './NewApplicationForm';
import '../styles/leagueManagement.css';

// Объявлена на уровне модуля намеренно: если определить её внутри
// SeasonSettingsFields, на каждый ввод React получит новый тип компонента,
// перемонтирует всё поддерево и сбросит скролл модалки
const Section = ({ title, subtitle, compact, children }) => {
    if (compact) {
        return (
            <div className="compactSection">
                <h3 className="compactSectionTitle">{title}</h3>
                {children}
            </div>
        );
    }
    return (
        <div className="settingsCard">
            <div className="settingsCardHeader">
                <h2>{title}</h2>
                <p>{subtitle}</p>
            </div>
            <div className="settingsCardBody">{children}</div>
        </div>
    );
};

// Поля league_settings. Используются и на вкладке сезона (compact = false,
// каждая секция в своей карточке), и в модалке создания сезона (compact = true).
export default function SeasonSettingsFields({
    form, onChange, compact, applications, onCreateApplication,
}) {
    const [newApp, setNewApp] = useState(null); // null = форма создания скрыта

    const groupStages = parseInt(form.group_stages_amount, 10);
    const appList = applications || [];
    const selectedApp = appList.find(a => String(a.id) === String(form.application_id));

    const submitNewApp = async (application) => {
        const created = await onCreateApplication(application);
        if (created && created.id) {
            onChange('application_id', String(created.id));
            setNewApp(null);
        }
    };

    return (
        <>
            <Section
                title="Формат турнира"
                subtitle="Как считается таблица и как устроена сетка."
                compact={compact}
            >
                <div className="lmRow">
                    <label className="modalLabel">Тип лиги</label>
                    <div className="segmented">
                        <button
                            type="button"
                            className={`segmentedItem ${form.league_type === 1 ? 'segmentedItemActive' : ''}`}
                            onClick={() => onChange('league_type', 1)}
                        >
                            <strong>Обычный чемпионат</strong>
                            <span>единая таблица</span>
                        </button>
                        <button
                            type="button"
                            className={`segmentedItem ${form.league_type === 2 ? 'segmentedItemActive' : ''}`}
                            onClick={() => onChange('league_type', 2)}
                        >
                            <strong>Группы + плей-офф</strong>
                            <span>групповой этап и сетка</span>
                        </button>
                    </div>
                </div>

                {form.league_type === 2 && (
                    <div className="lmRow lmRowNested">
                        <label className="modalLabel">Туров, идущих в группу</label>
                        <input
                            type="number"
                            className="modalInput inputNarrow"
                            min={1}
                            value={form.group_stages_amount}
                            onChange={(e) => onChange('group_stages_amount', e.target.value)}
                        />
                        <span className="fieldHint">
                            В группу идут стадии с порядковым номером от 1 до этого числа,
                            остальные — плей-офф. Добавили 4 тур и он должен войти в группу —
                            поставьте 4.
                        </span>
                        {(isNaN(groupStages) || groupStages <= 0) && (
                            <span className="fieldError">Для этого типа нужно указать количество туров.</span>
                        )}
                    </div>
                )}
            </Section>

            <Section
                title="Сложность лиги"
                subtitle="Влияет на турнирную таблицу и рейтинг команд."
                compact={compact}
            >
                <div className="lmRow">
                    <label className="modalLabel">Рейтинг лиги</label>
                    <div className="ratingScale">
                        {RATE_RATIOS.map(r => (
                            <button
                                type="button"
                                key={r.value}
                                className={`ratingItem ${form.rate_ratio === r.value ? 'ratingItemActive' : ''}`}
                                onClick={() => onChange('rate_ratio', r.value)}
                            >
                                <strong>{r.label}</strong>
                                <span>{r.hint}</span>
                            </button>
                        ))}
                    </div>
                    <div className="ratingFooter">
                        <span className="fieldHint">
                            Сила чемпионата. Влияет на то, сколько рейтинга команды получают
                            за матчи этого сезона.
                        </span>
                        {form.rate_ratio !== null && (
                            <button
                                type="button"
                                className="linkButton"
                                onClick={() => onChange('rate_ratio', null)}
                            >
                                очистить
                            </button>
                        )}
                    </div>
                </div>

                <div className="lmRow">
                    <div className="switchRow">
                        <div className="switchText">
                            <span className="switchTitle">Серия пенальти при ничьей</span>
                            <span className="fieldHint">
                                Ничья в основное время — победитель серии получает больше очков,
                                чем за ничью.
                            </span>
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={form.penalty_enabled}
                                onChange={(e) => onChange('penalty_enabled', e.target.checked)}
                            />
                            <span className="switchSlider" />
                        </label>
                    </div>
                </div>

                {form.penalty_enabled && (
                    <div className="lmRow lmRowNested">
                        <label className="modalLabel">Очков за победу по пенальти</label>
                        <input
                            type="number"
                            className="modalInput inputNarrow"
                            min={1}
                            max={3}
                            value={form.penalty_draw_score}
                            onChange={(e) => onChange('penalty_draw_score', e.target.value)}
                        />
                        <span className="fieldHint">
                            Обычно 2. Проигравший серию получает 1 очко, победа в основное время — 3.
                        </span>
                        {!(parseInt(form.penalty_draw_score, 10) > 0) && (
                            <span className="fieldError">Введите число больше 0.</span>
                        )}
                    </div>
                )}
            </Section>

            <Section
                title="Заявка"
                subtitle="Какая заявочная кампания привязана к сезону."
                compact={compact}
            >
                <div className="lmRow">
                    <label className="modalLabel">Название заявки</label>
                    <div className="lmApplicationRow">
                        <select
                            className="modalInput"
                            value={form.application_id}
                            onChange={(e) => onChange('application_id', e.target.value)}
                        >
                            <option value="" disabled>— выберите заявку —</option>
                            {appList.map(app => (
                                <option key={app.id} value={app.id}>
                                    {app.name}{app.year ? ` · ${app.year}` : ''}
                                </option>
                            ))}
                        </select>
                        {selectedApp && (
                            <span className="applicationCount">
                                {selectedApp.players_count !== undefined && selectedApp.players_count !== null
                                    ? `${selectedApp.players_count} игроков`
                                    : '—'}
                            </span>
                        )}
                    </div>
                    <span className="fieldHint">
                        {appList.length === 0
                            ? 'У клиента ещё нет заявок. Создайте первую кнопкой ниже.'
                            : 'Заявка определяет, из какого состава капитаны заявляют игроков на сезон.'}
                    </span>
                    {!(parseInt(form.application_id, 10) > 0) && (
                        <span className="fieldError">
                            Без заявки сезон не сохранится: капитанам некуда заявлять игроков.
                        </span>
                    )}
                </div>

                {newApp === null ? (
                    <div className="lmRow">
                        <button
                            type="button"
                            className="actionButtonGhost"
                            onClick={() => setNewApp(true)}
                        >
                            <FiPlus style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                            Создать новую заявку
                        </button>
                    </div>
                ) : (
                    <NewApplicationForm
                        existingNames={appList.map(a => a.name)}
                        onSubmit={submitNewApp}
                        onCancel={() => setNewApp(null)}
                    />
                )}

                <div className="lmRow">
                    <div className="switchRow">
                        <div className="switchText">
                            <span className="switchTitle">Трансферное окно открыто</span>
                            <span className="fieldHint">
                                Пока включено, капитаны могут подавать и менять заявки.
                            </span>
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={form.application_active}
                                onChange={(e) => onChange('application_active', e.target.checked)}
                            />
                            <span className="switchSlider" />
                        </label>
                    </div>
                </div>
            </Section>
        </>
    );
}
